// Variable to track the currently open select
let currentlyOpenSelect = null;
let isDocumentListenerAdded = false;

/**
 * Highlights option during keyboard navigation
 * @param {NodeList} listItems - list of all options
 * @param {number} index - index of option to highlight
 */
function highlightOption(listItems, index) {
  listItems.forEach((item, i) => {
    item.setAttribute('aria-selected', i === index);
  });
}

/**
 * Removes active state from all options in select (for single selection)
 * @param {NodeList} listItems - list of options
 */
function removeActiveStateFromAllOptionsInSelect(listItems) {
  listItems.forEach((option) => option.classList.remove('active'));
}

/**
 * Checks if select is a multiselect
 * @param {Element} select - select element
 * @returns {boolean}
 */
function isMultipleSelect(select) {
  return select.getAttribute('data-multiple') === 'true' || select.classList.contains('c-custom-select--multiple');
}

/**
 * Opens group if it contains active child elements
 * @param {Element} select - select element
 */
function openGroupWhenChildIsActive(select) {
  const groups = select.querySelectorAll('.c-custom-select__group');
  groups.forEach((group) => {
    if (group.querySelector('.c-custom-select__groups li.active')) {
      group.classList.add('grouped');
    }
  });
}

/**
 * Updates select display (for multiselect - count, for single - text)
 * @param {Element} select - select element
 * @param {NodeList} listItems - list of options
 */
function updateSelectDisplay(select, listItems) {
  const isMultiple = isMultipleSelect(select);
  const filterNameField = select.querySelector('.c-custom-select__filter');

  if (!filterNameField) return;

  // Get base text, removing previous counters and values
  const baseText = filterNameField.textContent
    .replace(/\s*\(\d+\)$/, '')
    .replace(/\s*\|\s*\d+\s*$/, '')
    .replace(/\s*\|\s*\d+\s*\|\s*\d+\s*$/, '')
    .replace(/\s*\|\s*\d+\s*\|\s*\d+\s*\|\s*\d+\s*$/, '')
    .split(' | ')[0]; // Take only base name

  if (isMultiple) {
    // For multiselect show count (include parent groups as selections)
    const activeFilters = Array.from(listItems).filter((el) => el.classList.contains('active')).length;

    const expectedText = activeFilters > 0 ? `${baseText} | ${activeFilters}` : baseText;

    if (filterNameField.textContent !== expectedText) {
      filterNameField.textContent = expectedText;
    }
  } else {
    // For single select show selected option text
    const activeOption = Array.from(listItems).find((el) => el.classList.contains('active') && !el.classList.contains('c-custom-select__group--sub'));

    if (activeOption) {
      filterNameField.textContent = activeOption.textContent.trim();
    } else {
      filterNameField.textContent = baseText;
    }
  }
}

/**
 * Closes select
 * @param {Element} select - select element to close
 */
function closeSelect(select) {
  if (select) {
    select.classList.remove('open');
    select.setAttribute('aria-expanded', false);
    if (currentlyOpenSelect === select) {
      currentlyOpenSelect = null;
    }
  }
}

/**
 * Closes all open selects on the page
 */
function closeAllSelects() {
  document.querySelectorAll('.c-custom-select.open').forEach((select) => {
    closeSelect(select);
  });
}

/**
 * Opens select and closes other open ones
 * @param {Element} select - select element to open
 */
function openSelect(select) {
  const isMobileSelect = select.querySelector('.c-custom-select__wrapper--mobile') !== null;

  if (!isMobileSelect && currentlyOpenSelect && currentlyOpenSelect !== select) {
    closeSelect(currentlyOpenSelect);
  }

  select.classList.add('open');
  select.setAttribute('aria-expanded', true);

  if (!isMobileSelect) {
    currentlyOpenSelect = select;
  }
  openGroupWhenChildIsActive(select);

  const destinationHidden = select.querySelector('input[type="hidden"][name="destination"]');
  if (destinationHidden && !destinationHidden.value) {
    const firstRegion = select.querySelector('.c-custom-select__group--sub');
    if (firstRegion) {
      firstRegion.classList.add('grouped');
    }
  }
}

/**
 * Clears all selected options in select
 * @param {Element} select - select element to clear
 */
function clearSelect(select) {
  const listItems = select.querySelectorAll('.c-custom-select__options li');
  const hiddenInput = select.querySelector('input[type="hidden"]');
  const filterNameField = select.querySelector('.c-custom-select__filter');
  const isMultiple = isMultipleSelect(select);
  const label = select.getAttribute('data-label');

  listItems.forEach((item) => {
    item.classList.remove('active');
    item.classList.remove('grouped');
  });

  if (hiddenInput) {
    hiddenInput.value = '';
  }

  // Restore base filter text
  if (filterNameField && label) {
    filterNameField.textContent = label;
  }

  closeSelect(select);

  // Update global filter state and apply changes
  const filterName = hiddenInput?.getAttribute('name');
  if (filterName && window.filterState) {
    if (isMultiple) {
      window.filterState[filterName] = [];
    } else {
      window.filterState[filterName] = '';
    }

    // Check if this is a mobile select
    const isMobileSelect = select.querySelector('.c-custom-select__wrapper--mobile') !== null;

    // Apply filters immediately only for desktop version
    if (!isMobileSelect && window.applyFilters) {
      window.applyFilters();
    }
  }
}

function cCustomSelect() {
  document.querySelectorAll('.c-custom-select:not(.is-initialized)').forEach((select) => {
    select.classList.add('is-initialized');
    const options = select.querySelector('.c-custom-select__options');
    const hiddenInput = select.querySelector('input[type="hidden"]');
    const listItems = options.querySelectorAll('li');
    const clearButton = select.querySelector('.c-custom-select__clear');

    let index = -1;

    select.addEventListener('focusout', (e) => {
      if (e.relatedTarget && !select.contains(e.relatedTarget)) {
        closeSelect(select);
      }
    });

    /**
     * Handles option selection (supports both single and multiple selection)
     * @param {Element} item - selected option
     * @param {Event} e - click event
     */
    function selectOption(item, e) {
      const link = item.querySelector('a');
      if (link && e.target !== link && !link.contains(e.target)) {
        link.click();
        return;
      }

      const href = link?.getAttribute('href');
      if (href && href !== '#' && link.closest('.c-custom-select--language')) {
        window.location.href = href;
        return;
      }

      e.stopPropagation();

      const isMultiple = isMultipleSelect(select);
      const isGroupItem = item.classList.contains('c-custom-select__group--sub');

      if (isGroupItem) {
        item.classList.toggle('grouped');
        listItems.forEach((li) => li.removeAttribute('aria-selected'));
        return;
      }

      const isActive = item.classList.contains('active');

      if (isMultiple) {
        if (isActive) {
          item.classList.remove('active');
        } else {
          item.classList.add('active');
        }
      } else if (!isActive) {
        removeActiveStateFromAllOptionsInSelect(listItems);
        item.classList.add('active');
      }

      // Update select display
      updateSelectDisplay(select, listItems);

      // Update hidden field value
      if (hiddenInput) {
        const rawValues = Array.from(select.querySelectorAll('.c-custom-select__options li.active'))
          .map((li) => li.dataset.value)
          .filter((value) => value);

        const filterName = hiddenInput.getAttribute('name');

        let activeOptions = rawValues;
        if (isMultiple && filterName === 'destination') {
          activeOptions = rawValues.reduce((acc, val) => {
            if (val.includes(',')) {
              val.split(',').forEach((v) => {
                const cleaned = v.trim();
                if (cleaned) acc.push(cleaned);
              });
              return acc;
            }
            acc.push(val);
            return acc;
          }, []);

          const activeLeafNow = Array.from(select.querySelectorAll('.c-custom-select__options li.active'))
            .filter((li) => !li.classList.contains('c-custom-select__group--sub')).length;
          if (activeLeafNow === 0) {
            activeOptions = [];
          }
        }

        if (isMultiple) {
          hiddenInput.value = activeOptions.join(',');
        } else {
          hiddenInput.value = activeOptions[0] || '';
        }

        // Update global filter state
        if (filterName && window.filterState) {
          if (isMultiple) {
            window.filterState[filterName] = activeOptions;
          } else {
            window.filterState[filterName] = activeOptions[0] || '';
          }

          const isMobileSelect = select.querySelector('.c-custom-select__wrapper--mobile') !== null;
          if (!isMobileSelect && window.applyFilters) {
            window.applyFilters();
          }
        }

        if (hiddenInput.closest('.c-custom-select--shop') && !hiddenInput.closest('.product-detail')) {
          const event = new Event('change', { bubbles: true });
          hiddenInput.dispatchEvent(event);
        }
      }

      // Remove keyboard highlight
      listItems.forEach((li) => li.removeAttribute('aria-selected'));

      // For single select close after selection, for multiselect keep open
      if (!isMultiple && !isActive) {
        closeSelect(select);
      }
    }

    /**
     * Select click handler (open/close)
     */
    select.addEventListener('click', (e) => {
      e.stopPropagation();

      // If clicked on clear button, don't handle as regular click
      if (e.target.closest('.c-custom-select__clear')) {
        return;
      }

      const isOpen = select.classList.contains('open');

      // Toggle open/close state
      if (isOpen) {
        closeSelect(select);
      } else {
        openSelect(select);
      }
    });

    /**
     * Select clear button handler
     */
    if (clearButton) {
      clearButton.addEventListener('click', (e) => {
        e.stopPropagation();
        clearSelect(select);
      });
    }

    /**
     * Keyboard navigation handler
     */
    select.addEventListener('keydown', (e) => {
      if (select.classList.contains('open') && (e.key === 'ArrowDown' || (e.key === 'Tab' && !e.shiftKey))) {
        e.preventDefault();
        index = (index + 1) % listItems.length;
        highlightOption(listItems, index);
        listItems[index].querySelector('a')?.focus();
      } else if (select.classList.contains('open') && (e.key === 'ArrowUp' || (e.key === 'Tab' && e.shiftKey))) {
        e.preventDefault();
        index = (index - 1 + listItems.length) % listItems.length;
        highlightOption(listItems, index);
        listItems[index].querySelector('a')?.focus();
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (select.classList.contains('open') && index >= 0) {
          removeActiveStateFromAllOptionsInSelect(listItems);
          selectOption(listItems[index], e);
        } else {
          openSelect(select);
        }
        select.focus();
      } else if (e.key === 'Escape') {
        closeSelect(select);
        select.focus();
      }
    });

    /**
     * Add click handlers for each option
     */
    listItems.forEach((item) => {
      item.addEventListener('click', (e) => selectOption(item, e));
    });
  });

  /**
   * Global click handler - closes selects when clicking outside their area
   */
  if (!isDocumentListenerAdded) {
    document.addEventListener('click', (e) => {
      const clickedSelect = e.target.closest('.c-custom-select');

      // If click was not on select, close all open selects
      if (!clickedSelect) {
        closeAllSelects();
      }
    });
    isDocumentListenerAdded = true;
  }
}

if (window.jQuery) {
  window.jQuery(document.body).on('updated_wc_div updated_cart_totals added_to_cart removed_from_cart wc_fragments_refreshed', () => {
    cCustomSelect();
  });
}

export default cCustomSelect;
